import React, { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { useAdminEnums } from '../../../../api/enums'
import { AdminReviewCard, EnumOption } from '../../../../api/types'
import { useCreateAdminReviewCard, useUpdateAdminReviewCard } from '../../../../api/review-cards'
import { FieldStack } from '../../../../components/form-details-fields/style'

interface ReviewCardValues {
  displayName: string
  description: string
  icon: string
  order: number
  immutable: boolean
}

const reviewCardSchema = yup.object({
  displayName: yup.string().required('Enter a display name'),
  description: yup.string().optional().default(''),
  icon: yup.string().optional().default(''),
  order: yup.number().typeError('Enter a valid number').min(0, 'Order must be 0 or more').required('Enter an order'),
  immutable: yup.boolean().default(false),
})

interface ReviewCardDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  formReferenceID: string
  versionReferenceID: string
  /** The card being edited, or null to create a new one. */
  card: AdminReviewCard | null
  /** All existing cards on the version — used to prevent duplicate order values. */
  existingCards: AdminReviewCard[]
  /** Default order for a new card (appended to the end). */
  nextOrder: number
  onSaved: () => void
}

/** Add/Edit drawer for a review card. Creates (POST) or updates (PUT) then triggers a builder refresh. */
export const ReviewCardDrawer: React.FC<ReviewCardDrawerProps> = ({
  isOpen,
  onOpenChange,
  formReferenceID,
  versionReferenceID,
  card,
  existingCards,
  nextOrder,
  onSaved,
}) => {
  const formId = 'review-card-form'
  const { data: enums } = useAdminEnums()
  const [createCard, creating] = useCreateAdminReviewCard(formReferenceID, versionReferenceID)
  const [updateCard, updating] = useUpdateAdminReviewCard(formReferenceID, versionReferenceID, card?.referenceID ?? '')
  const isBusy = creating || updating

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<ReviewCardValues>({
    resolver: yupResolver(reviewCardSchema),
    defaultValues: { displayName: '', description: '', icon: '', order: nextOrder, immutable: false },
  })

  const [iconOption, setIconOption] = useState<EnumOption | null>(null)

  // Reset the form to the card's values (or blank create defaults) each time the drawer opens.
  useEffect(() => {
    if (!isOpen) return
    reset({
      displayName: card?.displayName ?? '',
      description: card?.description ?? '',
      icon: card?.icon ?? '',
      order: card?.order ?? nextOrder,
      immutable: card?.immutable ?? false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, card])

  useEffect(() => {
    if (!isOpen) return
    setIconOption(card?.icon ? (enums?.Icon?.find((option) => option.value === card.icon) ?? null) : null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, card, enums])

  const handleClose = () => onOpenChange(false)

  const onSubmit: SubmitHandler<ReviewCardValues> = async (values) => {
    // Order must be unique across the version's cards (excluding the card being edited).
    const takenOrders = existingCards
      .filter((existing) => existing.referenceID !== card?.referenceID)
      .map((existing) => existing.order)
    if (takenOrders.includes(values.order)) {
      setError('order', {
        type: 'manual',
        message: `A review card already exists with order ${values.order}. Choose a different order.`,
      })
      return
    }

    try {
      if (card) await updateCard(values)
      else await createCard(values)
      onSaved()
      onOpenChange(false)
    } catch {
      // Errors surfaced via the hooks' toasts.
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={card ? 'Edit Review Card' : 'Add Review Card'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <InputGroup
            label="Display Name *"
            placeholder="e.g. Property Details"
            {...register('displayName')}
            errorMessage={errors.displayName?.message}
          />
          <InputGroup
            label="Description"
            placeholder="Optional description shown on the card"
            {...register('description')}
            errorMessage={errors.description?.message}
          />
          <Select
            label="Icon"
            isClearable
            options={enums?.Icon ?? []}
            value={iconOption}
            getOptionLabel={(option: EnumOption) => option.label}
            getOptionValue={(option: EnumOption) => option.value}
            onChange={(option: EnumOption | null) => {
              setIconOption(option)
              setValue('icon', option?.value ?? '')
            }}
            errorMessage={errors.icon?.message}
          />
          <InputGroup
            label="Order *"
            inputMode="numeric"
            {...register('order', { valueAsNumber: true })}
            errorMessage={errors.order?.message}
          />
          <Checkbox
            label="Immutable"
            supplementaryInfo="When selected, agents can view this card but cannot edit its fields."
            checked={Boolean(watch('immutable'))}
            onChange={() => setValue('immutable', !watch('immutable'))}
          />
        </FieldStack>
      )}
    </Drawer>
  )
}
